import type { MenuNavigation } from '@/models/types';
import {
  userHasSomePermissions,
  useUserHasSomePermissions,
  useUserPermissions,
} from '@/shared/auth/useUserAffectations';
import DirectusIcon from '@/shared/icons/DirectusIcon';
import { Box, Group, Menu, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import clsx from 'clsx';
import isObject from 'lodash/isObject';
import { NavLink, useLocation } from 'react-router-dom';
import classes from './AppMenubar.module.css';
import AppMenubarChildItem from './AppMenubarChildItem';

import { getItemLink } from '@/components/AppMenu/AppMenubar.utils';
import { IconExternalLink } from '@tabler/icons-react';

type Props = {
  item: MenuNavigation;
};

const AppMenubarItem = ({ item }: Props) => {
  const { pathname } = useLocation();

  const { permissions: userPermissions, isLoading } = useUserPermissions();

  const permission = isObject(item.permission) ? item.permission.id : item.permission;
  const { result: hasPermission } = useUserHasSomePermissions(permission ? [permission] : []);

  if (permission && !hasPermission) {
    return null;
  }

  const children = Array.isArray(item.children) ? (item.children as MenuNavigation[]) : [];
  const filteredChildren = children.filter(child => {
    if (child.status !== 'published') {
      return false;
    }

    const childPermission = isObject(child.permission) ? child.permission.id : child.permission;
    return !childPermission || userHasSomePermissions(userPermissions, [childPermission]);
  });

  const hasActiveRoute = (menuItem: MenuNavigation): boolean => {
    if (menuItem.linkType !== 'external_link' && menuItem.link) {
      const link = getItemLink(menuItem);
      if (pathname === link || pathname.startsWith(`${link}/`)) {
        return true;
      }
    }

    const nestedChildren = Array.isArray(menuItem.children)
      ? (menuItem.children as MenuNavigation[])
      : [];

    return nestedChildren.some(sub => {
      if (sub.status !== 'published') {
        return false;
      }

      const subPermission = isObject(sub.permission) ? sub.permission.id : sub.permission;
      if (subPermission && !userHasSomePermissions(userPermissions, [subPermission])) {
        return false;
      }

      return hasActiveRoute(sub);
    });
  };

  const isActive =
    filteredChildren.length > 0
      ? filteredChildren.some(sub => hasActiveRoute(sub))
      : item.linkType !== 'external_link' && pathname === getItemLink(item);

  if (isLoading) {
    return null;
  }

  if (filteredChildren.length > 0) {
    return (
      <Menu
        position="bottom-start"
        transitionProps={{
          transition: 'pop-top-left',
        }}
        offset={2}
      >
        <Menu.Target>
          <UnstyledButton
            className={clsx(classes.link, { [classes.linkActive]: isActive })}
            aria-haspopup="menu"
          >
            <Group gap="xs" wrap="nowrap" component="span">
              {item.icon && (
                <Box component="span" visibleFrom="xl" className={classes.linkIcon}>
                  <DirectusIcon icon={item.icon} size={20} />
                </Box>
              )}
              <span>{item.label}</span>
              <IconChevronDown size={14} stroke={1.5} className={classes.linkChevron} />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown className={classes.dropdown}>
          {filteredChildren.map(child => (
            <AppMenubarChildItem key={child.id} item={child} userPermissions={userPermissions} />
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }

  if (children.length > 0 && filteredChildren.length === 0) {
    return null;
  }

  if (!item.link) {
    return null;
  }

  const link = getItemLink(item);

  return (
    <NavLink
      to={link}
      className={({ isActive: navActive }) =>
        clsx(classes.link, { [classes.linkActive]: navActive })
      }
      target={item.linkType === 'external_link' ? '_blank' : undefined}
      rel={item.linkType === 'external_link' ? 'noopener noreferrer' : undefined}
    >
      <Group gap="xs" wrap="nowrap" component="span">
        {item.icon && (
          <Box component="span" visibleFrom="xl" className={classes.linkIcon}>
            <DirectusIcon icon={item.icon} size={20} />
          </Box>
        )}
        <span>{item.label}</span>
        {item.linkType === 'external_link' && (
          <IconExternalLink size={18} stroke={1.5} className={classes.linkExternal} />
        )}
      </Group>
    </NavLink>
  );
};

export default AppMenubarItem;
